import * as turf from '@turf/turf';
import { getDataURLByEchartOptions } from '@/utils';
import fetch from '@/utils/fetch';
import Vue from 'vue';

export function booleanPointInPolygon(point, poly) {
  const pt = turf.point(point);
  const polyI = turf.polygon(poly);

  return turf.booleanPointInPolygon(pt, polyI);
}

export function parseMapDataToGeoInfo(arr, type, needtrans = true) {
  let newArr = arr;

  if (needtrans) {
    newArr = arr.map(i => {
      return [i.lng || i.lon, i.lat];
    });
  }

  let res = JSON.stringify({
    type,
    coordinates: [newArr]
  });

  return res;
}

export function calcAverage(arr) {
  const length = arr.length;
  if (!length) return 0;
  let sum = arr.reduce((acc, c) => {
    return acc + c;
  }, 0);

  return sum / length;
}

export function parseGeoInfoToMapData(geoInfoString = '') {
  if (!geoInfoString) return [[]];
  const arr = JSON.parse(geoInfoString).coordinates || [];
  if (JSON.parse(geoInfoString).type == 'MultiPolygon') {
    return arr.map(j => {
      return j.map(i => {
        return [
          ...i.map(item => ({
            lng: item[0],
            lat: item[1]
          }))
        ];
      });
    });
  } else {
    return arr.map(i => {
      return [
        ...i.map(item => ({
          lng: item[0],
          lat: item[1]
        }))
      ];
    });
  }
}

export function isObject(o) {
  return Object.prototype.toString.call(o) === '[object Object]';
}

export function findDeepId(data) {
  if (Array.isArray(data)) {
    const firstItem = data[0];
    return findDeepId(firstItem);
  } else {
    if (isObject(data)) {
      return data.id;
    } else {
      return '';
    }
  }
}

export function findDeepByAttr(data, attr) {
  if (!data) return '';

  if (Array.isArray(data)) {
    return findDeepByAttr(data[0], attr);
  } else if (isObject(data)) {
    return data[attr] || '';
  }
}

export function getQueryString(data) {
  const str = Object.keys(data).reduce((acc, c) => {
    acc += (acc ? '&' : '?') + [c] + '=' + data[c];
    return acc;
  }, '');
  return str;
}

export async function requestChart({
  lat = '',
  lon = '',
  datasetCode = '',
  datasetHeights = '',
  datasetHeight = ''
}) {
  return new Promise(resolve => {
    const obj = {
      lon: +lon,
      lat: +lat,
      datasetCode,
      datasetHeight: +datasetHeight
    };
    const arr = [
      {
        name: 'weibullChar',
        url: '/windfarm/meso/point/report/getWeibull',
        params: {
          datasetHeight,
          lat,
          lon,
          datasetCode
        },
        getOption(res) {
          const { data } = res;
          const {
            speed,
            frequency,
            weibullA = '',
            weibullK = '',
            weibullData = []
          } = data;

          obj.weibullA = weibullA;
          obj.weibullK = weibullK;

          return {
            textStyle: {
              color: '#000'
            },
            grid: {
              width: '75%'
            },
            tooltip: {
              trigger: 'axis',
              formatter: '风速:{b0} m/s<br /> 高度:{c0} m'
            },
            xAxis: {
              name: '风速(m/s)',
              data: speed
            },
            yAxis: {
              name: '频率(%)',
              type: 'value'
            },
            series: [
              {
                data: weibullData,
                type: 'line',
                smooth: true
              },
              {
                data: frequency,
                type: 'bar'
              }
            ]
          };
        }
      },
      {
        name: 'windDayChart',
        url: '/windfarm/meso/point/report/getWindDay',
        params: {
          lat,
          lon,
          datasetCode
        },
        getOption(res) {
          const { data = {} } = res;
          const { data: _data } = data;

          const days = new Array(24).fill('').map((i, index) => index);
          const height = _data.map(i => i.height + 'm');

          return {
            legend: {
              show: true,
              data: height,
              textStyle: {
                color: '#000'
              }
            },
            textStyle: {
              color: '#000'
            },
            xAxis: {
              boundaryGap: false,
              type: 'category',
              max: 23,
              name: '时间',
              data: days,
              axisTick: {
                show: true
              },
              axisLine: {
                lineStyle: {
                  color: '#000'
                }
              }
            },
            yAxis: {
              splitNumber: 5,
              axisLine: {
                lineStyle: {
                  color: '#000'
                }
              },
              name: '风速(m/s)',
              type: 'value'
            },
            series: _data.map(i => {
              return {
                name: i.height + 'm',
                data: i.windSpeed,
                type: 'line'
              };
            })
          };
        }
      },
      {
        name: 'windDirectionRoseChart',
        url: '/windfarm/meso/point/report/getWindDirectionRose',
        style: {
          width: '400px',
          height: '400px'
        },
        params: {
          datasetCode,
          datasetHeight,
          lat,
          lon
        },
        getOption(res) {
          const { data } = res;
          const { sector, frequency } = data;

          return {
            textStyle: {
              color: '#000'
            },
            tooltip: {
              trigger: 'item',
              formatter: '角度:{b0} °<br /> 频率:{c0} %'
            },
            angleAxis: {
              type: 'category',
              data: sector,
              startAngle: 105.5
            },
            polar: {},
            radiusAxis: {
              z: 100
            },
            series: [
              {
                type: 'bar',
                data: frequency.map(i => toFixed(i, 2)),
                coordinateSystem: 'polar'
              }
            ]
          };
        }
      },
      {
        name: 'windEnergyRoseChart',
        url: '/windfarm/meso/point/report/getWindEnergyRose',
        style: {
          width: '400px',
          height: '400px'
        },
        params: {
          datasetCode,
          datasetHeight,
          lat,
          lon
        },
        getOption(res) {
          const { data } = res;
          const { sector, frequency } = data;

          return {
            textStyle: {
              color: '#000'
            },
            tooltip: {
              trigger: 'item',
              formatter: '角度:{b0} °<br /> 频率:{c0} %'
            },
            angleAxis: {
              type: 'category',
              data: sector,
              startAngle: 105.5
            },
            polar: {},
            radiusAxis: {
              z: 100
            },
            series: [
              {
                type: 'bar',
                data: frequency.map(i => toFixed(i, 2)),
                coordinateSystem: 'polar'
              }
            ]
          };
        }
      },
      {
        name: 'windMonthChart',
        url: '/windfarm/meso/point/report/getWindMonth',
        params: {
          lat,
          lon,
          datasetCode
        },
        getOption(res) {
          const { data } = res;
          const { data: _data = [] } = data;

          const height = _data.map(i => i.height + 'm');
          const mounths = new Array(12).fill('').map((i, index) => index + 1);
          return {
            legend: {
              show: true,
              data: height,
              textStyle: {
                color: '#000'
              }
            },
            textStyle: {
              color: '#000'
            },
            xAxis: {
              nameLocation: 'end',
              scale: true,
              boundaryGap: false,
              type: 'category',
              // max: 12,
              name: '月份',
              data: mounths,
              axisTick: {
                show: true
              },
              axisLine: {
                lineStyle: {
                  color: '#000'
                }
              }
            },
            yAxis: {
              splitNumber: 5,
              axisLine: {
                lineStyle: {
                  color: '#000'
                }
              },
              name: '风速(m/s)',
              type: 'value'
            },
            series: _data.map(i => {
              return {
                name: i.height + 'm',
                data: i.windSpeed,
                type: 'line'
              };
            })
          };
        }
      },
      {
        name: 'windShearChart',
        url: '/windfarm/meso/point/report/getWindShear',
        params: {
          datasetCode,
          datasetHeight: datasetHeights,
          lat,
          lon
        },
        getOption(res) {
          const { data } = res;
          const { windSpeed, shear = [], height = [] } = data;
          const heightArr = shear.map((item, index) => {
            return [item, height[index]];
          });

          return {
            grid: [
              {
                left: '10%',
                bottom: '20%',
                top: '20%',
                right: '20%'
              }
            ],
            xAxis: {
              type: 'value',
              name: '风速(m/s)',
              splitLine: {}
            },
            yAxis: {
              type: 'value',
              name: '高度(m)',
              splitLine: {}
            },
            series: [
              {
                name: '高度',
                type: 'scatter',
                data: windSpeed
              },
              {
                name: '高度',
                type: 'line',
                showSymbol: false,
                smooth: true,
                data: heightArr
              }
            ]
          };
        }
      }
    ];

    let count = 0;

    arr.forEach(i => {
      fetch(i.url, {
        params: {
          ...i.params
        }
      }).then(res => {
        const option = i.getOption(res);
        getDataURLByEchartOptions(option, i.style).then(url => {
          count++;
          obj[i.name] = url;
          if (count >= arr.length) {
            resolve(obj);
          }
        });
      });
    });
  });
}

export const itemTypeMap = new Map([
  [1, '点'],
  [2, '矩形'],
  [3, '多边形'],
  [4, '行政区']
]);

export function calcAreaByData(data, needtrans, push = true) {
  let revertPolygonData = data;

  if (needtrans) {
    revertPolygonData = data.map(item => {
      return [item.lng, item.lat];
    });
  }

  if (push) {
    revertPolygonData.push(revertPolygonData[0]);
  }

  return toFixed(
    turf.area(turf.polygon([revertPolygonData], { name: 'poly1' })) / 1000000,
    4
  );
}

export function toFixed(val, precision) {
  if (precision < 0) {
    return val;
  }

  const num = Number(val);
  if (window.isNaN(num)) return val;
  return num.toFixed(precision);
}

export function checkNumber(input) {
  if (input === '') return false;

  if (isNaN(input)) {
    return false;
  }

  return true;
}

export function checkIntergerNumber(value) {
  if (!/^[0-9]*$/.test(value)) {
    return false;
  }
  return true;
}
// 获取功率-推力-cp曲线图表
export const filterPowerCurveData = function(data) {
  let initObj = { power: [], value: [], thrust: [] };
  return data.reduce((init, item) => {
    init.power.push([item.wspd, item.power]);
    init.value.push([item.wspd, item.value]);
    init.thrust.push([item.wspd, item.thrust]);
    return init;
  }, initObj);
};

export const eventBus = new Vue();
